import React, { useState, useEffect } from 'react';
import { Button, Drawer } from 'aha-components';
import { isMobile } from '@/biz/tool';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import ModalAnalysisBreadcrumb from './ModalAnalysisBreadcrumb';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { NumericFormat } from 'react-number-format';
import PriceFieldsComponent from './PriceFieldsComponent';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import { OrderStatus } from '@/interface/order';
import axiosInstance from '../../../apis/axiosInstance';
import { useTranslation } from 'react-i18next';
import { MOBILE_DRAWER_HEIGHT } from '@/utils/viewportHeight';
import {
  resolveAhaPicksOfferItem,
  resolveAcceptOfferDisplayPrice
} from './AhaPicksPrePriceFields';

interface NumberFormatUSDProps {
  inputRef: (instance: any) => void;
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

function NumberFormatUSD(props: NumberFormatUSDProps) {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value
          }
        });
      }}
      thousandSeparator
      valueIsNumericString
      allowNegative={false}
      // suffix=" USD"
    />
  );
}

const ModalShowAnInterest: React.FC = () => {
  const { t } = useTranslation();
  const {
    showModalConfirmDealPrice,
    setShowModalConfirmDealPrice,
    setShowModalAcceptAhaPicksOffer
  } = useWebModalStore();
  const { detailData } = useDetailDataStore();

  if (!detailData?.order?.status.includes(OrderStatus.Invited)) {
    return null;
  }

  return (
    <>
      <Drawer
        width={810}
        title={t('modal.confirm_deal_price.title', {
          defaultValue: 'Confirm deal price'
        })}
        open={showModalConfirmDealPrice}
        onClose={() => {
          setShowModalConfirmDealPrice(false);
          setShowModalAcceptAhaPicksOffer(false);
        }}
        bodyStyle={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        placement={isMobile() ? 'bottom' : 'right'}
        height={isMobile() ? MOBILE_DRAWER_HEIGHT : undefined}
      >
        <AnalysisContent />
      </Drawer>
    </>
  );
};

const AnalysisContent: React.FC = () => {
  const { t } = useTranslation();
  const { detailData } = useDetailDataStore();
  const {
    showModalConfirmDealPrice,
    setShowModalConfirmDealPrice,
    setShowModalMissOut,
    setShowModalSubmitInterest,
    showModalAcceptAhaPicksOffer
  } = useWebModalStore();
  const { selectedPriceFields, setSelectedPriceFields, setRateNonNegotiable } =
    useMultiplePricesStore();

  const [newOrderPrice, setNewOrderPrice] = useState<number | string>('');
  const [nextLoading, setNextLoading] = useState(false);
  const [maxOrderPrice, setMaxOrderPrice] = useState<number | null>(null);
  const [nonNegotiable, setNonNegotiable] = useState(false);
  const [isPriceRangeValid, setIsPriceRangeValid] = useState(true);
  const [modalSession, setModalSession] = useState(0);

  useEffect(() => {
    if (!showModalConfirmDealPrice) {
      setNewOrderPrice('');
      setNonNegotiable(false);
      setIsPriceRangeValid(true);
      return;
    }

    setModalSession((session) => session + 1);

    if (showModalAcceptAhaPicksOffer) {
      const selected = useMultiplePricesStore.getState().selectedPriceFields[0];
      const offerItem = selected ?? resolveAhaPicksOfferItem(detailData?.order);
      const acceptPrice = resolveAcceptOfferDisplayPrice(offerItem);
      if (acceptPrice != null) {
        setNewOrderPrice(acceptPrice);
      }
      return;
    }

    if (detailData?.order?.fixedCreatorEarnAmount) {
      setNewOrderPrice(detailData.order.fixedCreatorEarnAmount);
      setNonNegotiable(true);
    }
  }, [
    showModalConfirmDealPrice,
    showModalAcceptAhaPicksOffer,
    detailData?.order
  ]);

  const nextClick = async () => {
    if (!isPriceRangeValid) return;

    if (showModalAcceptAhaPicksOffer) {
      setShowModalConfirmDealPrice(false);
      setShowModalSubmitInterest(true);
      return;
    }

    if (newOrderPrice && +newOrderPrice > 0 && selectedPriceFields.length > 0) {
      const params = {
        interestedPriceField: {
          ...selectedPriceFields[0],
          actualGet: +newOrderPrice
        }
      };
      setNextLoading(true);
      try {
        const { data } = await axiosInstance.post(
          `/order/${detailData?.order?.id}/competitivePrice`,
          params
        );
        setNextLoading(false);
        setSelectedPriceFields([data?.data?.interestedPriceField]);
        setRateNonNegotiable(nonNegotiable);
        setShowModalConfirmDealPrice(false);
        setShowModalSubmitInterest(true);
      } catch (error) {
        console.log('error', error);
      } finally {
        setNextLoading(false);
      }
    } else {
      setShowModalConfirmDealPrice(false);
      setShowModalSubmitInterest(true);
    }
  };

  return (
    <div className="w-full flex flex-col items-start gap-3 flex-1 min-h-0">
      <div className="shrink-0 w-full">
        <ModalAnalysisBreadcrumb index={1} />
      </div>
      <div className="w-full flex flex-col gap-3 flex-1 min-h-0 overflow-y-auto">
        <div className="text-[#1c2024] text-base font-semibold font-['Plus Jakarta Sans'] leading-snug">
          {t('modal.confirm_deal_price.select_content_type', {
            defaultValue: 'Select content type'
          })}{' '}
        </div>
        <PriceFieldsComponent
          key={modalSession}
          direction={isMobile() ? 'flex flex-col' : 'flex flex-row'}
          hasRadioButton={true}
          orderPriceAdjustment={true}
          newOrderPrice={newOrderPrice}
          setNewOrderPrice={setNewOrderPrice}
          maxOrderPrice={maxOrderPrice as number | null}
          nonNegotiable={nonNegotiable}
          setNonNegotiable={setNonNegotiable as (v: boolean) => void}
          isModal={true}
          onPriceRangeValidChange={setIsPriceRangeValid}
        />
      </div>
      {/*  */}
      <div
        className={`bg-white w-full flex items-center gap-4 border-t border-solid border-[#e8e8ec] pt-4 shrink-0
            ${isMobile() ? 'justify-center pb-[calc(16px+env(safe-area-inset-bottom,0px))]' : 'justify-end'}
          `}
      >
        <Button
          className="!w-[160px] !h-10"
          type="default"
          onClick={() => {
            setShowModalConfirmDealPrice(false);
            setShowModalMissOut(true);
          }}
        >
          {t('common.cancel', { defaultValue: 'Cancel' })}
        </Button>
        <Button
          className="!w-[160px] !h-10"
          type="primary"
          disabled={nextLoading || !newOrderPrice || !isPriceRangeValid}
          onClick={nextClick}
          loading={nextLoading}
        >
          {t('common.next', { defaultValue: 'Next' })}
        </Button>
      </div>
    </div>
  );
};

export default ModalShowAnInterest;
